<app-main-layout>
  <div class="bg-gray-50 min-h-screen">
  <div class="py-6 px-4 sm:px-6 max-w-[1400px] mx-auto">

    <!-- ============ HERO ============ -->
    <div class="relative overflow-hidden rounded-3xl bg-neutral-900 text-white px-6 sm:px-8 py-7 mb-6 shadow-lg">
      <div class="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full bg-amber-400/20 blur-3xl"></div>
      <div class="pointer-events-none absolute right-10 bottom-0 opacity-[0.07]"><mat-icon class="!text-[160px] !h-[160px] !w-[160px]">local_offer</mat-icon></div>

      <div class="relative flex flex-col lg:flex-row lg:items-end justify-between gap-6">
        <div class="max-w-xl">
          <div class="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.15em] text-amber-400">
            <mat-icon class="!text-[14px] !h-3.5 !w-3.5">groups</mat-icon> Customer hub
          </div>
          <h1 class="mt-2 text-3xl font-bold tracking-tight">Discount Coupons</h1>
          <p class="mt-2 text-sm text-white/60 leading-relaxed">Codes customers and agents can apply to an order - how much each has been used, and when it stops working.</p>
        </div>
        <div class="flex flex-wrap items-center gap-2 self-start lg:self-auto">
          <a routerLink="/reports/coupons" class="flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-white/[0.08] border border-white/10 text-sm font-semibold hover:bg-white/[0.14] transition-colors">
            <mat-icon class="!text-[18px] !h-[18px] !w-[18px] text-amber-300">insights</mat-icon> Coupon report
          </a>
          <button type="button"
            class="group flex items-center gap-2.5 pl-2.5 pr-5 py-2.5 rounded-2xl bg-amber-400 text-neutral-900 font-bold text-sm shadow-lg shadow-amber-500/20 hover:bg-amber-300 hover:-translate-y-0.5 active:translate-y-0 transition-all focus:outline-none focus:ring-4 focus:ring-amber-400/40"
            (click)="onEditClick('ADD')">
            <span class="h-8 w-8 rounded-xl bg-neutral-900 text-amber-400 flex items-center justify-center transition-transform duration-200 group-hover:rotate-90">
              <mat-icon class="!text-[20px] !h-5 !w-5">add</mat-icon>
            </span>
            New coupon
          </button>
        </div>
      </div>

      <div class="relative mt-7 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        @for (s of [
          { key: 'active', label: 'Active codes', value: stats?.active, icon: 'local_offer', tone: 'text-emerald-300', on: 'bg-emerald-400/15 border-emerald-400/50' },
          { key: 'expiring', label: 'Expiring in 14 days', value: stats?.expiring, icon: 'hourglass_bottom', tone: 'text-amber-300', on: 'bg-amber-400/15 border-amber-400/50' },
          { key: 'usedUp', label: 'Used up', value: stats?.usedUp, icon: 'block', tone: 'text-rose-300', on: 'bg-rose-400/15 border-rose-400/50' },
          { key: 'expired', label: 'Expired', value: stats?.expired, icon: 'event_busy', tone: 'text-white/50', on: 'bg-white/[0.14] border-white/30' }
        ]; track s.key) {
        <button type="button" class="text-left rounded-2xl border px-4 py-3 transition-colors" (click)="setStatus($any(s.key))"
          [ngClass]="status === s.key ? s.on : 'bg-white/[0.06] border-white/10 hover:bg-white/[0.1]'">
          <div class="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide" [ngClass]="s.tone"><mat-icon class="!text-[14px] !h-3.5 !w-3.5">{{ s.icon }}</mat-icon>{{ s.label }}</div>
          <div class="mt-1 text-2xl font-bold tabular-nums">{{ s.value ?? '–' }}</div>
        </button>
        }
        <div class="rounded-2xl bg-amber-400 text-neutral-900 px-4 py-3 col-span-2 sm:col-span-1">
          <div class="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-neutral-900/60"><mat-icon class="!text-[14px] !h-3.5 !w-3.5">confirmation_number</mat-icon>Redeemed · 30 days</div>
          <div class="mt-1 text-2xl font-bold tabular-nums">{{ stats?.redemptions30d ?? '–' }} <span class="text-sm font-semibold text-neutral-900/50">{{ stats?.redemptions ?? 0 | number }} ever</span></div>
        </div>
      </div>
    </div>

    <!-- ============ TOOLBAR ============ -->
    <div class="sticky top-0 z-20 -mx-4 sm:mx-0 px-4 sm:px-0 py-3 mb-4 bg-gray-50/90 backdrop-blur">
      <div class="flex flex-col lg:flex-row lg:items-center gap-2.5">
        <div class="relative flex-1 min-w-0">
          <mat-icon class="absolute left-3.5 top-1/2 -translate-y-1/2 !text-gray-400 !text-[20px] !h-5 !w-5 pointer-events-none">search</mat-icon>
          <input type="text" placeholder="Search by code or description…"
            class="w-full pl-11 pr-10 py-2.5 rounded-xl border border-gray-200 bg-white shadow-sm focus:border-amber-400 focus:ring-4 focus:ring-amber-100 outline-none transition-all text-sm"
            name="coupon-name" [(ngModel)]="couponKeyword" (ngModelChange)="onSearchInput()" />
          @if (couponKeyword) {
          <button class="absolute right-2.5 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700 flex items-center justify-center" (click)="clearSearch()" aria-label="Clear search">
            <mat-icon class="!text-[18px] !h-[18px] !w-[18px]">close</mat-icon>
          </button>
          }
        </div>
        <div class="flex flex-wrap items-center gap-2">
          <select class="h-10 px-3 rounded-xl border border-gray-200 bg-white shadow-sm text-xs font-semibold text-gray-700 outline-none focus:border-amber-400 cursor-pointer" [ngModel]="kind" (ngModelChange)="setKind($event)" aria-label="Type">
            @for (k of kindOptions; track k.value) { <option [value]="k.value">{{ k.label }}</option> }
          </select>
          <select class="h-10 px-3 rounded-xl border border-gray-200 bg-white shadow-sm text-xs font-semibold text-gray-700 outline-none focus:border-amber-400 cursor-pointer" [ngModel]="sort" (ngModelChange)="setSort($event)" aria-label="Sort">
            @for (o of sortOptions; track o.value) { <option [value]="o.value">{{ o.label }}</option> }
          </select>
        </div>
      </div>
      <div class="mt-2.5 flex flex-wrap items-center gap-2">
        <div class="flex flex-wrap bg-white border border-gray-200 rounded-xl p-1 gap-1 shadow-sm">
          @for (o of statusOptions; track o.value) {
          <button class="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all"
            [ngClass]="status === o.value ? 'bg-neutral-900 text-white shadow-sm' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'" (click)="setStatus(o.value)">
            {{ o.label }} <span class="tabular-nums" [ngClass]="status === o.value ? 'text-amber-400' : 'text-gray-400'">{{ countFor(o.value) }}</span>
          </button>
          }
        </div>
        @if (filtersActive) {
          <button class="flex items-center gap-1 text-xs font-semibold text-amber-700 hover:underline px-1" (click)="resetFilters()"><mat-icon class="!text-[14px] !h-3.5 !w-3.5">filter_alt_off</mat-icon>Clear all</button>
        }
        <span class="ml-auto text-xs text-gray-400">{{ loading ? 'Loading…' : (count | number) + ' coupon' + (count === 1 ? '' : 's') }}</span>
      </div>
    </div>

    @if (loadError) {
      <div class="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-2xl p-4 text-sm flex items-center justify-between">
        <span class="flex items-center gap-2"><mat-icon class="!text-[18px] !h-[18px] !w-[18px]">cloud_off</mat-icon>Could not load coupons from the server.</span>
        <button class="text-xs uppercase font-semibold underline" (click)="load()">Try again</button>
      </div>
    }

    <!-- ============ LIST ============ -->
    @if (loading && coupons.length === 0) {
      <div class="flex flex-col gap-2.5">
        @for (i of [1, 2, 3, 4, 5]; track i) {
        <div class="flex items-center gap-4 rounded-2xl bg-white border border-gray-100 p-4 animate-pulse">
          <div class="h-16 w-24 rounded-xl bg-gray-100"></div>
          <div class="flex-1"><div class="h-4 w-40 rounded bg-gray-100"></div><div class="mt-2 h-3 w-1/3 rounded bg-gray-100"></div><div class="mt-2 flex gap-1.5"><div class="h-5 w-20 rounded-md bg-gray-100"></div><div class="h-5 w-24 rounded-md bg-gray-100"></div></div></div>
          <div class="hidden md:block h-2 w-32 rounded-full bg-gray-100"></div>
        </div>
        }
      </div>
    } @else if (coupons.length === 0) {
      <div class="rounded-3xl border-2 border-dashed border-gray-200 bg-white py-16 text-center">
        <span class="mx-auto h-14 w-14 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center"><mat-icon class="!text-[28px] !h-7 !w-7">{{ filtersActive ? 'search_off' : 'local_offer' }}</mat-icon></span>
        <p class="mt-3 text-sm font-semibold text-gray-700">{{ filtersActive ? 'No coupons match these filters' : 'No coupons yet' }}</p>
        <p class="text-xs text-gray-400">{{ filtersActive ? 'Try a different search or clear the filters' : 'Create the first one' }}</p>
        @if (filtersActive) { <button class="mt-4 text-xs font-semibold text-amber-700 hover:underline" (click)="resetFilters()">Clear all filters</button> }
      </div>
    } @else {
      <div class="flex flex-col gap-2.5 transition-opacity" [class.opacity-50]="loading">
        @for (c of coupons; track c.id) {
        <div role="button" tabindex="0" (click)="view(c)" (keydown.enter)="view(c)"
          class="group relative flex rounded-2xl bg-white border cursor-pointer transition-all duration-200 hover:-translate-y-px hover:shadow-[0_8px_24px_-12px_rgba(0,0,0,0.18)] focus:outline-none focus-visible:ring-4 focus-visible:ring-amber-100 overflow-hidden"
          [ngClass]="selected?.id === c.id ? 'border-amber-300 ring-4 ring-amber-100/80' : 'border-gray-100 hover:border-gray-200'">

          <!-- Ticket stub: the discount -->
          <div class="relative w-24 sm:w-28 flex-shrink-0 flex flex-col items-center justify-center py-4"
            [ngClass]="isLive(c) ? 'bg-gradient-to-br from-amber-300 to-amber-500 text-neutral-900' : 'bg-gray-100 text-gray-400'">
            <span class="text-2xl font-extrabold leading-none tabular-nums">{{ valueLabel(c) }}</span>
            <span class="text-[10px] font-bold uppercase tracking-[0.2em] mt-1 opacity-70">off</span>
            @if (c.giveAway) { <mat-icon class="mt-1 !text-[16px] !h-4 !w-4 opacity-70" matTooltip="Comes with a free gift">redeem</mat-icon> }
            <!-- tear line -->
            <span class="absolute -right-2 -top-2 h-4 w-4 rounded-full bg-gray-50 border border-gray-100"></span>
            <span class="absolute -right-2 -bottom-2 h-4 w-4 rounded-full bg-gray-50 border border-gray-100"></span>
          </div>
          <div class="w-px border-l-2 border-dashed border-gray-200 my-3"></div>

          <div class="flex-1 min-w-0 grid grid-cols-[minmax(0,1fr)_auto] lg:grid-cols-[minmax(0,1fr)_190px_150px_auto] gap-x-5 gap-y-3 items-center px-4 sm:px-5 py-3.5" [class.opacity-70]="!isLive(c)">

            <!-- Code, description, rules -->
            <div class="min-w-0">
              <div class="flex items-center gap-2 flex-wrap">
                <button class="font-mono font-bold text-[15px] text-gray-900 hover:text-amber-700 flex items-center gap-1 text-left break-all" (click)="$event.stopPropagation(); copyCode(c)" [matTooltip]="copied === c.couponCode ? 'Copied' : 'Copy code'">
                  {{ c.couponCode }}
                  <mat-icon class="!text-[14px] !h-[14px] !w-[14px]" [ngClass]="copied === c.couponCode ? 'text-emerald-500' : 'text-gray-300'">{{ copied === c.couponCode ? 'check' : 'content_copy' }}</mat-icon>
                </button>
                <span class="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-md" [ngClass]="statusStyle(c).cls">
                  <span class="h-1.5 w-1.5 rounded-full" [ngClass]="statusStyle(c).dot"></span>{{ statusStyle(c).label }}
                </span>
              </div>
              @if (c.couponDesc) { <div class="text-sm text-gray-500 mt-0.5 line-clamp-1">{{ c.couponDesc }}</div> }
              <div class="mt-2 flex flex-wrap items-center gap-1.5">
                @for (r of rules(c).slice(0, 5); track r.text) {
                  <span class="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md"
                    [ngClass]="r.tone === 'violet' ? 'bg-violet-50 text-violet-700' : r.tone === 'sky' ? 'bg-sky-50 text-sky-700' : 'bg-gray-50 border border-gray-100 text-gray-600'">
                    <mat-icon class="!text-[13px] !h-[13px] !w-[13px]">{{ r.icon }}</mat-icon>{{ r.text }}
                  </span>
                }
                @if (rules(c).length > 5) { <span class="text-[11px] font-semibold text-amber-700">+{{ rules(c).length - 5 }} more</span> }
                @if (rules(c).length === 0) { <span class="text-[11px] text-gray-300">No conditions</span> }
              </div>
            </div>

            <!-- Actions (top right on phones) -->
            <div class="lg:order-last flex items-center gap-0.5 rounded-xl bg-gray-50 border border-gray-100 p-0.5 self-start lg:self-center lg:opacity-0 lg:group-hover:opacity-100 lg:group-focus-within:opacity-100 transition-opacity"
              [class.lg:opacity-100]="selected?.id === c.id">
              <button class="h-8 w-8 rounded-lg text-gray-500 hover:bg-white hover:text-gray-900 hover:shadow-sm flex items-center justify-center" (click)="$event.stopPropagation(); onEditClick('EDIT', c)" matTooltip="Edit" aria-label="Edit"><mat-icon class="!text-[17px] !h-[17px] !w-[17px]">edit</mat-icon></button>
              <button class="h-8 w-8 rounded-lg text-gray-500 hover:bg-white hover:text-sky-700 hover:shadow-sm flex items-center justify-center" (click)="$event.stopPropagation(); onDuplicateClick(c)" matTooltip="Duplicate as a new coupon" aria-label="Duplicate"><mat-icon class="!text-[17px] !h-[17px] !w-[17px]">file_copy</mat-icon></button>
              <button class="h-8 w-8 rounded-lg text-gray-500 hover:bg-red-50 hover:text-red-600 flex items-center justify-center" (click)="$event.stopPropagation(); onDeleteClick(c)" matTooltip="Delete" aria-label="Delete"><mat-icon class="!text-[17px] !h-[17px] !w-[17px]">delete</mat-icon></button>
            </div>

            <!-- Usage -->
            <div class="col-span-2 lg:col-span-1">
              <div class="flex items-baseline justify-between text-xs">
                <span><span class="font-bold text-gray-900 text-sm tabular-nums">{{ c.used ?? 0 | number }}</span> <span class="text-gray-400">used</span></span>
                <span class="text-gray-400">{{ limited(c.useTimes) ? 'of ' + (c.useTimes | number) : 'unlimited' }}</span>
              </div>
              <div class="mt-1.5 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                @if (limited(c.useTimes)) {
                  <div class="h-full rounded-full transition-all" [ngClass]="usagePercent(c) >= 100 ? 'bg-rose-500' : usagePercent(c) >= 75 ? 'bg-amber-500' : 'bg-emerald-500'" [style.width.%]="Math.max(usagePercent(c), c.used ? 3 : 0)"></div>
                } @else {
                  <div class="h-full rounded-full bg-sky-400/70" [style.width.%]="c.used ? Math.max((c.used / maxUsed) * 100, 3) : 0"></div>
                }
              </div>
              <div class="text-[11px] text-gray-400 mt-1">{{ c.lastUsed ? 'Last used ' + lastUsedAgo(c) : 'Never used' }}</div>
            </div>

            <!-- Expiry -->
            <div class="col-span-2 lg:col-span-1 lg:text-right">
              <div class="text-xs font-semibold" [ngClass]="c.status === 'expired' ? 'text-gray-500' : c.status === 'expiring' ? 'text-amber-700' : 'text-gray-800'">
                {{ expiryInfo(c).label }} @if (expiryInfo(c).note) { <span class="font-normal text-gray-500">{{ expiryInfo(c).note }}</span> }
              </div>
              @if (hasRealExpiry(c)) { <div class="text-[11px] text-gray-400 mt-0.5">{{ c.expiryDate | date : 'd MMM y' }}</div> }
            </div>
          </div>
        </div>
        }
      </div>

      <div class="mt-4 rounded-2xl bg-white border border-gray-100 shadow-sm overflow-hidden">
        <mat-paginator [length]="count" [pageSize]="pageSize" [pageIndex]="pageIndex"
          [pageSizeOptions]="[10, 20, 50]" showFirstLastButtons (page)="onPage($event)">
        </mat-paginator>
      </div>
    }
  </div>
  </div>

  <!-- ============ VIEW (slide-over) ============ -->
  @if (selected; as c) {
  <div class="fixed inset-0 z-40 bg-black/30 backdrop-blur-[1px] dc-fade" (click)="selected = null"></div>
  <aside class="fixed inset-y-0 right-0 z-50 w-full max-w-md bg-white shadow-2xl flex flex-col dc-slide" role="dialog" aria-modal="true" aria-label="Coupon">
    <div class="flex-1 overflow-y-auto">
      <div class="relative overflow-hidden bg-neutral-900 text-white px-6 pt-6 pb-5">
        <div class="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-amber-400/20 blur-2xl"></div>
        <div class="relative flex items-start justify-between gap-3">
          <div class="min-w-0">
            <div class="text-[11px] font-bold uppercase tracking-[0.15em] text-amber-400">Coupon</div>
            <button class="mt-1.5 font-mono text-2xl font-bold break-all flex items-center gap-2 hover:text-amber-300" (click)="copyCode(c)">
              {{ c.couponCode }}<mat-icon class="!text-[18px] !h-[18px] !w-[18px] text-white/40">{{ copied === c.couponCode ? 'check' : 'content_copy' }}</mat-icon>
            </button>
            @if (c.couponDesc) { <p class="mt-1 text-sm text-white/70">{{ c.couponDesc }}</p> }
          </div>
          <button type="button" class="h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center flex-shrink-0" (click)="selected = null" aria-label="Close"><mat-icon class="!text-[18px] !h-[18px] !w-[18px]">close</mat-icon></button>
        </div>
        <div class="relative mt-5 grid grid-cols-3 gap-2">
          <div class="rounded-xl bg-amber-400 text-neutral-900 px-3 py-2"><div class="text-[10px] uppercase tracking-wide opacity-60">Discount</div><div class="text-lg font-extrabold tabular-nums">{{ valueLabel(c) }}</div></div>
          <div class="rounded-xl bg-white/[0.07] px-3 py-2"><div class="text-[10px] uppercase tracking-wide text-white/50">Used</div><div class="text-lg font-bold tabular-nums">{{ c.used ?? 0 | number }}<span class="text-xs text-white/40">{{ limited(c.useTimes) ? ' / ' + c.useTimes : '' }}</span></div></div>
          <div class="rounded-xl bg-white/[0.07] px-3 py-2"><div class="text-[10px] uppercase tracking-wide text-white/50">Status</div><div class="text-sm font-bold mt-1">{{ statusStyle(c).label }}</div></div>
        </div>
        <div class="relative mt-4 flex flex-wrap gap-2">
          <button type="button" class="flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-amber-400 text-neutral-900 text-xs font-bold hover:bg-amber-300" (click)="onEditClick('EDIT', c)"><mat-icon class="!text-[16px] !h-4 !w-4">edit</mat-icon>Edit</button>
          <button type="button" class="flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-white/10 text-white text-xs font-semibold hover:bg-white/20" (click)="onDuplicateClick(c)"><mat-icon class="!text-[16px] !h-4 !w-4">file_copy</mat-icon>Duplicate</button>
          <button type="button" class="flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-white/10 text-white text-xs font-semibold hover:bg-red-600" (click)="onDeleteClick(c)"><mat-icon class="!text-[16px] !h-4 !w-4">delete</mat-icon>Delete</button>
        </div>
      </div>

      <div class="p-6 flex flex-col gap-6">
        @if (limited(c.useTimes)) {
          <div>
            <div class="flex items-center justify-between text-xs mb-1.5"><span class="text-gray-500">Uses left</span><span class="font-bold text-gray-900">{{ Math.max(c.useTimes - (c.used ?? 0), 0) | number }} of {{ c.useTimes | number }}</span></div>
            <div class="h-2.5 rounded-full bg-gray-100 overflow-hidden"><div class="h-full rounded-full" [ngClass]="usagePercent(c) >= 100 ? 'bg-rose-500' : usagePercent(c) >= 75 ? 'bg-amber-500' : 'bg-emerald-500'" [style.width.%]="usagePercent(c)"></div></div>
          </div>
        }

        <div class="grid grid-cols-2 gap-2">
          <div class="rounded-xl bg-gray-50 px-3 py-2.5">
            <div class="text-[10px] uppercase tracking-wide text-gray-400">Expiry</div>
            <div class="text-sm font-semibold" [ngClass]="c.status === 'expiring' ? 'text-amber-700' : 'text-gray-800'">{{ expiryInfo(c).label }} {{ expiryInfo(c).note }}</div>
            @if (hasRealExpiry(c)) { <div class="text-[11px] text-gray-400">{{ c.expiryDate | date:'d MMM y' }}</div> }
          </div>
          <div class="rounded-xl bg-gray-50 px-3 py-2.5">
            <div class="text-[10px] uppercase tracking-wide text-gray-400">Last used</div>
            <div class="text-sm font-semibold text-gray-800">{{ c.lastUsed ? (c.lastUsed | date:'d MMM y') : 'Never' }}</div>
            @if (c.lastUsed) { <div class="text-[11px] text-gray-400">{{ lastUsedAgo(c) }}</div> }
          </div>
        </div>

        <div>
          <div class="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-2">Conditions</div>
          @if (rules(c).length) {
            <div class="flex flex-col gap-1.5">
              @for (r of rules(c); track r.text) {
                <div class="flex items-center gap-2.5 px-3 py-2 rounded-xl border border-gray-100 text-sm"
                  [ngClass]="r.tone === 'violet' ? 'bg-violet-50/60 text-violet-800' : r.tone === 'sky' ? 'bg-sky-50/60 text-sky-800' : 'text-gray-700'">
                  <mat-icon class="!text-[17px] !h-[17px] !w-[17px] opacity-60">{{ r.icon }}</mat-icon>{{ r.text }}
                </div>
              }
            </div>
          } @else {
            <div class="rounded-xl bg-gray-50 py-5 text-center text-sm text-gray-400">No conditions - works on any order</div>
          }
        </div>

        @if ($any(c.couponCategories)?.length || $any(c.couponProducts)?.length) {
          <div>
            <div class="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-2">Applies to</div>
            <div class="grid grid-cols-2 gap-2">
              @for (g of [{ label: 'Categories', icon: 'category', list: $any(c.couponCategories) ?? [] }, { label: 'Products', icon: 'inventory_2', list: $any(c.couponProducts) ?? [] }]; track g.label) {
                <div class="rounded-xl border border-gray-100 px-3 py-2.5">
                  <div class="flex items-center gap-1.5 text-xs font-semibold text-gray-700"><mat-icon class="!text-[15px] !h-[15px] !w-[15px] text-gray-400">{{ g.icon }}</mat-icon>{{ g.label }}</div>
                  <div class="mt-1 text-[11px] text-gray-500">
                    <span class="font-semibold text-emerald-700">{{ includedCount(g.list) }} included</span>
                    @if (g.list.length - includedCount(g.list)) { · <span class="font-semibold text-rose-600">{{ g.list.length - includedCount(g.list) }} excluded</span> }
                  </div>
                </div>
              }
            </div>
            <p class="mt-1.5 text-[11px] text-gray-400">Open the coupon to see or change exactly which ones.</p>
          </div>
        }
      </div>
    </div>
  </aside>
  }
</app-main-layout>
